import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { FiSearch } from "react-icons/fi"

import { OrganizationsService } from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { PageShell, PageSkeleton } from "@/components/Layout"
import AddOrganization from "@/components/Organizations/AddOrganization"
import OrganizationAPIKeys from "@/components/Organizations/OrganizationAPIKeys"
import OrganizationSettings from "@/components/Organizations/OrganizationSettings"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"

export const Route = createFileRoute("/_layout/organizations/")({
  component: OrganizationsPage,
})

function OrganizationsTable() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => OrganizationsService.listOrganizations(),
  })

  if (isLoading) {
    return <PageSkeleton variant="table" rows={4} />
  }

  const organizations = data?.data ?? []

  if (organizations.length === 0) {
    return (
      <AppEmptyState
        title={emptyStateCopy.organizations.none.title}
        description={emptyStateCopy.organizations.none.description}
        icon={<FiSearch />}
        data-testid="organizations-empty-state"
      />
    )
  }

  return (
    <>
      {/* Desktop */}
      <Box display={{ base: "none", md: "block" }}>
        <Table.Root size={{ base: "sm", md: "md" }}>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader w="30%">Name</Table.ColumnHeader>
              <Table.ColumnHeader w="25%">Slug</Table.ColumnHeader>
              <Table.ColumnHeader w="15%">Members</Table.ColumnHeader>
              <Table.ColumnHeader w="30%">Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {organizations.map((org) => (
              <Table.Row key={org.id}>
                <Table.Cell w="30%">{org.name}</Table.Cell>
                <Table.Cell w="25%">
                  <Badge colorPalette="blue">{org.slug}</Badge>
                </Table.Cell>
                <Table.Cell w="15%">
                  <Text color="fg.muted">N/A</Text>
                </Table.Cell>
                <Table.Cell w="30%">
                  <Flex gap={2} wrap="wrap">
                    <OrganizationAPIKeys
                      orgId={org.id}
                      orgName={org.name}
                      appId={org.app_id}
                    />
                    <OrganizationSettings
                      orgId={org.id}
                      orgName={org.name}
                      currentSettings={org.settings}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        navigate({
                          to: "/organizations/$organizationId/members",
                          params: { organizationId: org.id },
                        })
                      }
                    >
                      Members
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        navigate({
                          to: "/organizations/$organizationId/branding",
                          params: { organizationId: org.id },
                        })
                      }
                    >
                      Branding
                    </Button>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Mobile cards */}
      <VStack display={{ base: "flex", md: "none" }} gap={3} align="stretch">
        {organizations.map((org) => (
          <Card.Root key={org.id}>
            <Card.Body>
              <Text fontWeight="medium">{org.name}</Text>
              <Badge colorPalette="blue" mt={1}>
                {org.slug}
              </Badge>
              <Flex gap={2} wrap="wrap" mt={3}>
                <OrganizationAPIKeys
                  orgId={org.id}
                  orgName={org.name}
                  appId={org.app_id}
                />
                <OrganizationSettings
                  orgId={org.id}
                  orgName={org.name}
                  currentSettings={org.settings}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    navigate({
                      to: "/organizations/$organizationId/members",
                      params: { organizationId: org.id },
                    })
                  }
                >
                  Members
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    navigate({
                      to: "/organizations/$organizationId/branding",
                      params: { organizationId: org.id },
                    })
                  }
                >
                  Branding
                </Button>
              </Flex>
            </Card.Body>
          </Card.Root>
        ))}
      </VStack>
    </>
  )
}

function OrganizationsPage() {
  const breadcrumbs = useRouteBreadcrumbs("organizations.index")

  return (
    <PageShell title="Organizations" breadcrumbs={breadcrumbs}>
      <AddOrganization />
      <OrganizationsTable />
    </PageShell>
  )
}
